import { Button, Card, Modal } from '@safetyhub/ui';
import type { ConflictExistingMember, MemberConflictMatchedBy } from '@safetyhub/shared';

export interface PendingContactInput {
  name: string;
  phone: string;
  email?: string;
  department?: string;
  position?: string;
}

export interface MemberPhoneConflictModalProps {
  isOpen: boolean;
  existingMember: ConflictExistingMember;
  pendingInput: PendingContactInput;
  /**
   * 무엇이 겹쳤는지. 생략 시 `'phone'`(구 서버 응답 호환).
   * 이메일만 겹친 경우에만 "다른 사람입니다" 선택지가 뜬다 — 대표 메일 공유가 흔해
   * 전화번호와 같은 무게로 볼 수 없기 때문.
   */
  matchedBy?: MemberConflictMatchedBy;
  /**
   * 선택을 처리하는 중(담당자 생성 요청 진행 중). true면 모든 선택지를 잠근다 —
   * 이게 없으면 "○○님으로 등록"을 두 번 눌렀을 때 같은 담당자가 2건 만들어진다.
   * 닫기 수단(오버레이·Esc·✕)도 함께 막는다(SiteContactFormModal의 제출 중 규약과 동일).
   */
  isProcessing?: boolean;
  onSelectExisting: (memberId: string) => void;
  /** 이메일만 겹친 경우 "다른 사람입니다 — 새로 등록" */
  onCreateAsDifferentPerson?: () => void;
  onCancel: () => void;
}

function isDuplicateRole(
  existingMember: ConflictExistingMember,
  department: string | undefined,
  position: string | undefined,
): boolean {
  const normDept = department?.trim() || null;
  const normPos = position?.trim() || null;
  return existingMember.siteContacts.some(
    (sc) =>
      sc.isActive &&
      (sc.department || null) === normDept &&
      (sc.position || null) === normPos,
  );
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

/**
 * 인원 중복 시 표시하는 sub 모달 (z-[80]).
 * 기존 Member 재사용 / (이메일 중복만) 다른 사람으로 등록 / 다시 입력 중 선택.
 */
export function MemberPhoneConflictModal({
  isOpen,
  existingMember,
  pendingInput,
  matchedBy = 'phone',
  isProcessing = false,
  onSelectExisting,
  onCreateAsDifferentPerson,
  onCancel,
}: MemberPhoneConflictModalProps) {
  const isDup = isDuplicateRole(
    existingMember,
    pendingInput.department,
    pendingInput.position,
  );
  // 이메일만 겹친 경우에만 "다른 사람" 선택지를 준다. 전화번호가 같으면 같은 사람으로 본다.
  const canCreateAsDifferentPerson = matchedBy === 'email' && !!onCreateAsDifferentPerson;

  const pendingRoleLabel =
    [pendingInput.department, pendingInput.position].filter(Boolean).join(' / ');
  const hasPendingRole = pendingRoleLabel.length > 0;

  return (
    // 이 모달은 **항상** SiteContactFormModal(bg-black/40) 위에 겹쳐 뜨는 sub라 오버레이를
    // 정본 /40이 아닌 /25로 낮춘다. 둘 다 /40이면 합성이 0.64가 되어 리스킨 전(gray-500/75 2겹)
    // 보다 오히려 어두워진다. /25면 합성 명도가 리스킨 전과 사실상 같다.
    <Modal
      open={isOpen}
      onClose={onCancel}
      title={matchedBy === 'email' ? '이미 등록된 이메일' : '이미 등록된 전화번호'}
      size="md"
      overlayZClassName="z-[80]"
      overlayBgClassName="bg-black/25"
      closeOnOverlay={!isProcessing}
      closeOnEsc={!isProcessing}
      showCloseButton={!isProcessing}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={isProcessing}>
            다시 입력
          </Button>
          {canCreateAsDifferentPerson && (
            <Button
              variant="secondary"
              onClick={onCreateAsDifferentPerson}
              disabled={isProcessing}
            >
              다른 사람입니다
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => onSelectExisting(existingMember.id)}
            disabled={isDup || isProcessing}
          >
            {isProcessing ? '등록 중...' : `${existingMember.name}님으로 등록`}
          </Button>
        </>
      }
    >
      {matchedBy === 'email' ? (
        <p className="text-sm text-ink-600 mb-4">
          <span className="font-medium">{pendingInput.email}</span> 이메일은 이미{' '}
          <span className="font-medium text-ink-900">{existingMember.name}</span>님
          {existingMember.phone ? `(${formatPhone(existingMember.phone)})` : ''}으로 등록되어
          있습니다. 전화번호는 서로 다릅니다.
        </p>
      ) : (
        <p className="text-sm text-ink-600 mb-4">
          <span className="font-medium">{formatPhone(pendingInput.phone)}</span> 번호는 이미{' '}
          <span className="font-medium text-ink-900">{existingMember.name}</span>님으로 등록되어 있습니다.
        </p>
      )}

      {/* 기존 Member 카드 */}
      <Card surface="soft" elevation="none" className="mb-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-9 w-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
            <span className="text-brand-700 font-medium text-sm">
              {existingMember.name.charAt(0)}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink-900">{existingMember.name}</p>
            {existingMember.email && (
              <p className="text-xs text-ink-500 truncate">{existingMember.email}</p>
            )}
          </div>
        </div>
        {existingMember.siteContacts.filter((sc) => sc.isActive).length > 0 && (
          <div>
            <p className="text-xs font-medium text-ink-500 mb-1">현재 등록된 역할</p>
            <ul className="space-y-0.5">
              {existingMember.siteContacts
                .filter((sc) => sc.isActive)
                .map((sc, idx) => {
                  const roleLabel = [sc.department, sc.position].filter(Boolean).join(' / ');
                  return (
                    <li key={idx} className="text-xs text-ink-700">
                      {sc.siteName}
                      <span className={roleLabel ? 'text-ink-500' : 'text-ink-400'}>
                        {' - '}
                        {roleLabel || '역할 미지정'}
                      </span>
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
      </Card>

      {/* 역할 확인 질문 — dup일 때는 숨김 (모순 방지) */}
      {!isDup && (
        <p className="text-sm text-ink-700">
          {hasPendingRole ? (
            <>
              <span className="font-medium">{existingMember.name}</span>님이{' '}
              <span className="font-medium text-brand-700">[{pendingRoleLabel}]</span>{' '}
              역할도 맡습니까?
            </>
          ) : (
            <>
              <span className="font-medium">{existingMember.name}</span>님으로 추가 역할 지정 없이 등록하시겠습니까?
            </>
          )}
        </p>
      )}

      {/* 중복 역할 경고 */}
      {isDup && (
        <div className="rounded-card bg-amber-50 border border-amber-200 px-3 py-2">
          <p className="text-xs text-amber-700">
            {existingMember.name}님은 이미 동일 역할로 등록되어 있어 추가 등록이 불필요합니다.
          </p>
        </div>
      )}
    </Modal>
  );
}
